import {
  Box,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  ListSubheader,
  Stack,
  Typography,
  useTheme,
} from "@mui/material";
import { useContext, useEffect, useMemo, useState } from "react";

import AvatarWithStatus from "@/components/AvatarWithStatus";
import MemberDrawerListItem from "@/components/MemberDrawerListItem";
import { mapForHasWorkspaceMembershipIdMany } from "@/models/caching";
import { DataContext } from "@/models/DataProvider";
import { CurrentFeedContext } from "@/models/StateProviders/currentFeedProvider";
import { WorkspaceContext } from "@/models/StateProviders/workspaceProvider";
import "../assets/menus.css";
import Loading from "@/components/Loading/Index";
import { useReactiveSelectQuery } from "@/models/hooks/useReactiveSelectQuery.ts";
import { feedPermissionsQuery } from "@/models/repositories";

export default function MembersDrawerContents({ newDesign }: { newDesign?: boolean }) {
  const { currentWorkspaceId, accountMap } = useContext(WorkspaceContext);
  const { currentFeedId, currentFeedPendingInvites, currentFeed } = useContext(CurrentFeedContext);
  const { workspaceMembershipMap } = useContext(WorkspaceContext);
  const { fetchFeedPermissions } = useContext(DataContext);

  useEffect(() => {
    if (currentFeed && !currentFeed.loadedPermissions) {
      console.log("FETCHING PERMISSIONS");
      fetchFeedPermissions(currentFeed.id);
    }
  }, [currentFeed]);

  const feedPermissions = useReactiveSelectQuery(
    feedPermissionsQuery,
    [{ feedId: currentFeedId }],
    "MembersDrawerContents.feedPermissionsQuery",
  );

  const permissionMap = useMemo(
    () =>
      mapForHasWorkspaceMembershipIdMany(
        feedPermissions as Array<{ workspaceMembershipId: string; id: string; name: string; enabled: boolean }>,
      ),
    [feedPermissions],
  );

  const readPermissions = (
    feedPermissions as Array<{ name: string; enabled: boolean; workspaceMembershipId: string }>
  )?.filter((perm) => perm?.name === "read" && perm.enabled);

  const mutatedAccounts = useMemo(() => {
    return readPermissions
      ?.map((permission) => {
        const accountWithStatus = workspaceMembershipMap.get((permission as any)?.workspaceMembershipId);
        if (!accountWithStatus) {
          return null;
        }
        const perms = permissionMap.get((permission as any)?.workspaceMembershipId) as Array<{
          id: string;
          name: string;
          enabled: boolean;
        }>;
        const isAdmin = !!perms?.find((perm) => perm?.name === "admin" && perm?.enabled);
        const isMuted = !perms?.find((perm) => perm?.name === "write" && perm?.enabled);
        return {
          ...accountWithStatus,
          isAdmin,
          isMuted,
        };
      })
      ?.filter((account) => account !== null)
      ?.sort((a, b) => {
        const aSort = a?.name || a?.firstName || a?.email || a?.phoneNumber || a?.id;
        const bSort = b?.name || b?.firstName || b?.email || b?.phoneNumber || b?.id;
        return aSort?.localeCompare(bSort);
      });
  }, [readPermissions, workspaceMembershipMap, permissionMap, accountMap]);

  const theme = useTheme();

  const [panelId, setPanelId] = useState<string | null>(null);

  const MemberListHeader = ({ children }: { children: React.ReactNode }) => (
    <ListSubheader
      sx={{
        p: 0,
        background: theme.palette.secondary.dark,
        width: "100%",
        zIndex: 10,
      }}
    >
      <ListItem
        sx={{
          color: theme.palette.primary.main,
          borderRadius: "8px",
          padding: 1,
          border: "1.5px solid transparent",
        }}
        component="div"
      >
        <Typography
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            width: "100%",
          }}
          component="div"
        >
          <>{children}</>
        </Typography>
      </ListItem>
    </ListSubheader>
  );

  return (
    <>
      <Box
        sx={{
          height: newDesign ? "calc(100vh - 38px)" : "100%",
          background: theme.palette.secondary.dark,
          borderRadius: 4,
          overflow: "hidden",
        }}
      >
        <Stack
          sx={{
            p: 2,
            pr: 0.75,
            gap: 3.75,
            height: "100%",
          }}
        >
          <List
            key={`${currentWorkspaceId}-${currentFeedId}-members`}
            className="scrollable-content"
            sx={{
              display: "flex",
              alignItems: "center",
              flexDirection: "column",
              width: "100%",
              gap: 3.75,
              position: "relative",
              pt: 0,
              "& li": {
                width: "100%",
              },
              "& ul": {
                padding: 0,
                width: "100%",
              },
            }}
          >
            <li key={`section-${currentWorkspaceId}-${currentFeedId}-active-members`}>
              <ul key={`${currentWorkspaceId}-${currentFeedId}-active-members-list`}>
                <MemberListHeader>
                  <Typography sx={{ fontSize: "20px", fontWeight: 700 }}>Active members</Typography>
                </MemberListHeader>
                <Box>
                  {mutatedAccounts?.length > 0 ? (
                    <Box>
                      {mutatedAccounts?.map((account) => {
                        return (
                          <Box
                            key={account?.id}
                            sx={{
                              display: "flex",
                              width: "100%",
                              position: "relative",
                              mb: newDesign ? 0 : 1,
                              p: newDesign ? 0 : 1,
                              alignItems: "center",
                            }}
                            className={`${account?.id === panelId && "active-parent"}`}
                          >
                            <MemberDrawerListItem account={account} />
                          </Box>
                        );
                      })}
                    </Box>
                  ) : (
                    <Box sx={{ alignItems: "center", display: "flex" }}>
                      <Box sx={{ alignItems: "center", display: "flex", gap: 1 }}>
                        <Loading baseSize={10} /> <Box sx={{ color: theme.palette.neutral.main }}>Loading Users</Box>
                      </Box>
                    </Box>
                  )}
                </Box>
              </ul>
            </li>
            {currentFeedPendingInvites?.length > 0 && (
              <li key={`section-${currentWorkspaceId}-${currentFeedId}-pending-members`}>
                <ul style={{ padding: 0 }} key={`${currentWorkspaceId}-${currentFeedId}-pending-members-list`}>
                  <MemberListHeader>
                    <Typography sx={{ fontSize: "20px", fontWeight: 700 }}>Pending members</Typography>
                  </MemberListHeader>
                  <Box>
                    {currentFeedPendingInvites?.map((invite) => {
                      return (
                        <Box
                          key={invite?.id}
                          sx={{
                            display: "flex",
                            width: "100%",
                            position: "relative",
                            mb: 1,
                            p: 1,
                            alignItems: "center",
                          }}
                        >
                          <ListItem
                            className="menu-options-list"
                            key={invite?.id}
                            sx={{
                              p: 1,
                            }}
                          >
                            <ListItemIcon>
                              <AvatarWithStatus size={30} accountId={null} />
                            </ListItemIcon>
                            <ListItemText
                              sx={{
                                flexGrow: 0,
                                display: "-webkit-box",
                                WebkitLineClamp: 1,
                                WebkitBoxOrient: "vertical",
                                wordBreak: "break-word",
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                              }}
                              primaryTypographyProps={{
                                fontWeight: 500,
                              }}
                            >
                              {invite?.email || invite?.phoneNumber}
                            </ListItemText>
                          </ListItem>
                        </Box>
                      );
                    })}
                  </Box>
                </ul>
              </li>
            )}
          </List>
        </Stack>
      </Box>
    </>
  );
}
